/**
 * Relationships section for managing table joins
 *
 * Displays a read-only list of relationships in the sidebar.
 * Uses a dialog for create/edit operations.
 */

import { useState } from 'react';
import { Pencil, Trash2, Plus, Link2 } from 'lucide-react';
import type { TableFieldApiResponse } from '@loader/shared-types';
import { Button, Badge } from '@loader/ui';

import { RelationshipDialog } from '@/features/linkedTables/editLinkedTable/components/RelationshipDialog';
import type { LinkedTableRelationship } from '@/lib/types';

interface RelationshipsSectionProps {
  relationships: LinkedTableRelationship[];
  availableFields: TableFieldApiResponse[];
  currentLinkedTableId?: string;
  currentTableId?: string;
  /** Alias of the primary table (tables[0]) for new relationships */
  primaryTableAlias?: string;
  incompleteRelationshipIds?: string[];
  /** Whether the user can edit relationships (default: true) */
  canEdit?: boolean;
  onAddRelationship?: (relationship: LinkedTableRelationship) => void;
  onUpdateRelationship?: (relationship: LinkedTableRelationship) => void;
  onRemoveRelationship?: (relationshipId: string) => void;
}

/**
 * Format join type for display
 */
function formatJoinType(joinType: string): string {
  const upper = joinType.toUpperCase();
  switch (upper) {
    case 'LEFT':
      return 'LEFT';
    case 'INNER':
      return 'INNER';
    case 'RIGHT':
      return 'RIGHT';
    case 'FULL':
      return 'FULL';
    default:
      return upper;
  }
}

/**
 * Section for managing relationships between tables/linkedTables
 * Shows a compact list view with dialog for editing
 */
export function RelationshipsSection({
  relationships,
  availableFields,
  currentLinkedTableId,
  currentTableId,
  primaryTableAlias,
  incompleteRelationshipIds = [],
  canEdit = true,
  onAddRelationship,
  onUpdateRelationship,
  onRemoveRelationship,
}: RelationshipsSectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRelationship, setEditingRelationship] =
    useState<LinkedTableRelationship | null>(null);

  const hasCallbacks = onAddRelationship && onUpdateRelationship && onRemoveRelationship;
  const isEditable = canEdit && hasCallbacks;

  const handleAdd = () => {
    setEditingRelationship(null);
    setDialogOpen(true);
  };

  const handleEdit = (relationship: LinkedTableRelationship) => {
    setEditingRelationship(relationship);
    setDialogOpen(true);
  };

  const handleDelete = (relationshipId: string) => {
    onRemoveRelationship?.(relationshipId);
  };

  const handleSave = (relationship: LinkedTableRelationship) => {
    if (editingRelationship) {
      onUpdateRelationship?.(relationship);
    } else {
      onAddRelationship?.(relationship);
    }
    setDialogOpen(false);
    setEditingRelationship(null);
  };

  return (
    <div className="space-y-3">
      {/* Relationship List */}
      {relationships.length === 0 ? (
        <div className="text-muted-foreground py-4 text-center text-sm">
          No relationships defined
        </div>
      ) : (
        <div className="space-y-2">
          {relationships.map((rel) => {
            const isIncomplete = incompleteRelationshipIds.includes(rel.id);
            const mappingCount = rel.field_mappings?.length || 0;

            return (
              <div
                key={rel.id}
                className={`group flex items-center justify-between rounded-md border p-2 ${
                  isIncomplete
                    ? 'border-destructive bg-destructive/5 border-2'
                    : 'border-border bg-background'
                }`}
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <Link2 className="text-muted-foreground h-4 w-4 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium">
                        {rel.target_name || rel.alias}
                      </span>
                      <Badge variant="secondary" className="shrink-0 text-xs">
                        {formatJoinType(rel.join_type)}
                      </Badge>
                    </div>
                    <div className="text-muted-foreground truncate text-xs">
                      {mappingCount} field{mappingCount !== 1 ? 's' : ''} mapped
                      {rel.alias && rel.alias !== rel.target_name && (
                        <span className="ml-1">· as {rel.alias}</span>
                      )}
                    </div>
                  </div>
                </div>

                {isEditable && (
                  <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleEdit(rel)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive h-7 w-7"
                      onClick={() => handleDelete(rel.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Button */}
      {isEditable && (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={handleAdd}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add Relationship
        </Button>
      )}

      {/* Create/Edit Dialog */}
      <RelationshipDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        relationship={editingRelationship}
        availableFields={availableFields}
        currentLinkedTableId={currentLinkedTableId}
        currentTableId={currentTableId}
        primaryTableAlias={primaryTableAlias}
        onSave={handleSave}
      />
    </div>
  );
}
